import type {
  WepsterConfig,
  WepsterSettings as WepsterSettingsType,
} from '../../settings.integrations/wepster/types';
import type { AdditionalContact } from '../../types';
import type { RichLead } from '../types';

import { intlTel } from '../../services/intlTel';
import { logger } from '../../services/logger';
import { notify } from '../../ui';

const iconMap = {
  telegram: 'telegram',
  viber: 'viber',
  alphasms: 'viber',
  echat: 'viber',
  instagram: 'instagram',
  whatsapp: 'whatsapp',
  facebook: 'messenger',
  vkontakte: 'vkontakte',
};

export class ChatButtonController {
  static $inject = ['$scope', 'WepsterSettings'];

  // injected from directive link
  declare fallbackCountry: string;
  declare locale: string;
  declare email: string;
  declare $el: JQLite;

  declare lead: RichLead;

  /**
   * lead instance or lead.additional_contact to init button for
   */
  declare contact: AdditionalContact;
  declare settings: ReturnType<WepsterSettingsType['get']>;

  chatActive = false;
  chatLoading = false;
  errorMessage: string | null = null;
  icon: string = '';
  messengerPhone: RichLead['wepster_contact'] | null = null;
  messengerType: RichLead['wepster_messenger'] | null = null;

  /**
   * external link to wepster site for the active wepster_id
   */
  wepsterLink: string | null = null;

  phones: string[] = [];

  /**
   * the list of all phones available for lead, including additional contacts.
   * Used to switch between contacts when chat is active
   */
  allPhones: string[] = [];

  availableMessengers: { type: string; icon: string }[] = [];

  $drawer: JQLite | null = null;

  constructor(
    private $scope: angular.IScope,
    private WepsterSettings: WepsterSettingsType,
  ) {
    this.closeChat();
    this.setMessengerType(null);
    this.settings = WepsterSettings.getCached();

    this.settings.$promise
      .then(async (settings) => {
        await this.init(settings);

        // watch for contact and wepster data changes and update chat availability conditions
        $scope.$watch(
          () =>
            [this.lead.wepster_contact]
              .concat(
                [this.lead, ...(this.lead.additional_contacts || [])].map(
                  getContactFingerprint,
                ),
              )
              .join(''),
          (prevVal, nextVal) => {
            if (prevVal === nextVal) {
              return;
            }

            this.init(settings);
          },
        );
      })
      .catch((error) => {
        notify.error('Error initializing wepster chat', error);
      });
  }

  async init(settings: WepsterConfig) {
    if (!settings.wepster.isEnabled) {
      return;
    }

    const { lead, contact } = this;
    const availableMessengers = Object.entries(settings)
      .filter(([type, item]) => item.canWriteFirst && !!iconMap[type])
      .map(([type]) => ({ type, icon: iconMap[type] }));

    const canWriteFirst = Object.values(settings).some(
      (item) => item.canWriteFirst,
    );

    if (
      (!lead.wepster_id && !canWriteFirst) ||
      // TODO: we can remove vm.email check
      // when we will be sure that all users have fresh session
      !this.email
    ) {
      this.$el.hide();

      return;
    }

    this.allPhones = (
      await Promise.all(
        [lead, ...(lead.additional_contacts || [])].map((item) =>
          resolvePhones({
            contact: item,
            fallbackCountry: this.fallbackCountry,
          }),
        ),
      )
    ).flat();

    const wepsterContact =
      (await normalizePhone({
        phone: lead.wepster_contact,
        fallbackCountry: this.fallbackCountry,
      })) || '';

    if (wepsterContact && !this.allPhones.includes(wepsterContact)) {
      // current lead does not include wepster_contact so we need reset
      // wepster settings to allow user to bind another contact
      lead.wepster_contact = '';
      lead.wepster_id = '';
      lead.wepster_messenger = '';
    }

    const phones = await resolvePhones({
      contact,
      fallbackCountry: this.fallbackCountry,
    });

    this.setMessengerType(lead.wepster_messenger);
    this.messengerPhone = wepsterContact;
    this.availableMessengers = availableMessengers;
    this.phones = phones;

    if (
      !phones.length ||
      (lead.wepster_id && !phones.includes(wepsterContact))
    ) {
      // looks like wepster bound to different `contact`. Skip showing button
      this.$el.hide();

      return;
    }

    this.$el.show();
  }

  async openChat(phone?: string | null) {
    const { $drawer, messengerType, lead, contact } = this;

    if (!$drawer || !messengerType) {
      return;
    }

    this.chatActive = true;
    this.chatLoading = true;
    this.errorMessage = null;
    $drawer.find('.js-chat-iframe').empty();

    let wepsterId = lead.wepster_id;

    if (!wepsterId || phone) {
      try {
        if (!phone) {
          throw new Error('No phone number provided');
        }

        const config: {
          token?: string;
          templateName?: string;
          templateLanguageCode?: string;
          [key: string]: any;
        } = this.settings[messengerType];

        ({ wepster_id: wepsterId } = await this.WepsterSettings.addContact({
          // NOTE: some integrations like facebook does not have tokens
          // but they still should be usable for write first feature
          connectionId: config.token || '',
          templateName: config.templateName,
          templateLanguageCode: config.templateLanguageCode,
          channelType: messengerType,
          name: contact.name || 'No Name',
          phone,
        }).$promise);

        lead.wepster_id = wepsterId;
        lead.wepster_contact = phone;
        lead.wepster_messenger = messengerType;
      } catch (error) {
        logger.error('Error adding wepster contact', {
          error,
        });

        let errorMessage = 'Error adding wepster contact';

        if (error.data && error.data.message) {
          errorMessage += `: ${error.data.message}`;
        } else if (error.message) {
          errorMessage += `: ${error.message}`;
        }

        this.$scope.$apply(() => {
          this.errorMessage = errorMessage;
          this.chatLoading = false;
        });

        return;
      }
    }

    const iframe = document.createElement('iframe');
    iframe.classList.add('wepster-chat__iframe');
    iframe.onload = () => {
      this.$scope.$apply(() => {
        this.chatLoading = false;
        this.wepsterLink = `https://my.wepster.com/${this.settings.wepster.businessId}/chats/common/${lead.wepster_id}`;
      });
    };

    let locale = 'ua';

    // wepster uses 'ua' to identify uk locale
    if (this.locale && this.locale !== 'uk') {
      ({ locale } = this);
    }

    iframe.src = `https://my.wepster.com/login-with-token/${[
      this.email,
      this.settings.wepster.token,
      this.settings.wepster.businessId,
      wepsterId,
      locale,
    ].join('/')}`;

    // lets wait till angular renders everything
    setTimeout(() => {
      $drawer.find('.js-chat-iframe').append(iframe);
    });
  }

  closeChat() {
    this.chatActive = false;
    this.chatLoading = true;
    this.errorMessage = null;
    this.setMessengerType(this.lead.wepster_messenger);
  }

  setMessengerType(messengerType: RichLead['wepster_messenger'] | null) {
    this.messengerType = messengerType;
    this.icon = resolveIcon(messengerType || '');
  }

  setDrawerRef({ $el }: { $el: JQLite | null }) {
    this.$drawer = $el;
  }
}

const resolveIcon = (messengerType: string) => iconMap[messengerType] || 'note';

async function resolvePhones({
  contact,
  fallbackCountry,
}: {
  contact: AdditionalContact;
  fallbackCountry: string;
}): Promise<string[]> {
  const phones: string[] = [];

  await Promise.all(
    ([] as string[])
      .concat(contact.phone || [])
      .concat(
        (contact.contacts || [])
          // NOTE: this filtration is not reliable
          // because backend may set type phone for urls
          // TODO: commented out because when frontend adds a new contact,
          // it won't get type property
          // .filter((item) => item.type === 'phone')
          .map((item) => item.value),
      )
      .map(async (phone) => {
        const formattedPhone = await normalizePhone({
          phone,
          fallbackCountry,
        });

        if (formattedPhone) {
          phones.push(formattedPhone);
        }
      }),
  );

  return phones;
}

async function normalizePhone({
  phone,
  fallbackCountry,
}: {
  phone: string;
  fallbackCountry: string;
}): Promise<string | null> {
  const formattedPhone = await intlTel.parseMobileNumber(phone, {
    country: fallbackCountry,
  });

  // remove leading '+' sign
  return formattedPhone && formattedPhone.replace(/^\+/, '');
}

const getContactFingerprint = (contact: AdditionalContact): string =>
  [contact.phone]
    .concat((contact.contacts || []).map((item) => item.value))
    .join('');
